/**
 * Eén conceptsignaal: wat de database zegt, wat de bron zegt, de brontekst
 * ernaast, en het besluit.
 *
 * De drie blokken staan in die volgorde omdat het besluit pas te nemen is als
 * het bewijs eronder staat. De brontekst is geen versiering: zonder dat fragment
 * is een signaal een bewering zonder herkomst — hetzelfde gat dat de
 * documenteisen van migratie 069 hadden.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, Check, ExternalLink, X } from 'lucide-react';
import {
  heeftVoorstel,
  vervolgActie,
  type Besluit,
  type BronSignaal,
  type SignaalErnst,
} from '@/hooks/useSubsidieSignalen';

const ERNST_STIJL: Record<SignaalErnst, string> = {
  urgent: 'bg-red-100 text-red-800',
  waarschuwing: 'bg-amber-100 text-amber-900',
  info: 'bg-slate-100 text-slate-800',
};

const ERNST_RAND: Record<SignaalErnst, string> = {
  urgent: 'border-red-300 border-2',
  waarschuwing: 'border-amber-300 border-2',
  info: '',
};

function datumTekst(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function SignaalKaart({
  signaal,
  magAfhandelen,
  isBezig,
  onAfhandelen,
  onNaarBeheer,
}: {
  signaal: BronSignaal;
  magAfhandelen: boolean;
  isBezig: boolean;
  onAfhandelen: (invoer: { id: string; besluit: Besluit; notitie: string | null }) => void;
  onNaarBeheer: () => void;
}) {
  const [notitie, setNotitie] = useState('');
  const vervolg = vervolgActie(signaal);
  const voorstel = heeftVoorstel(signaal);

  return (
    <Card className={ERNST_RAND[signaal.ernst]}>
      <CardContent className="py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={ERNST_STIJL[signaal.ernst]}>{signaal.ernst}</Badge>
          {signaal.regeling_naam && <Badge variant="outline">{signaal.regeling_naam}</Badge>}
          <Badge variant="secondary">{signaal.soort.replace(/_/g, ' ')}</Badge>
          <span className="text-xs text-muted-foreground ml-auto">
            Eerst gezien {datumTekst(signaal.eerst_gezien_op)}
          </span>
        </div>

        <h3 className="font-semibold">{signaal.titel}</h3>
        {signaal.toelichting && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">{signaal.toelichting}</p>
        )}

        {/* De diff. Twee kolommen naast elkaar, want het hele punt van dit
            scherm is dat je ze in één oogopslag vergelijkt. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-md border p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Wat de database zegt
            </p>
            {signaal.veld && <p className="text-xs text-muted-foreground mt-1">{signaal.veld}</p>}
            <p className="mt-1 text-sm font-mono break-words">
              {signaal.huidige_waarde ?? <span className="text-muted-foreground italic">leeg</span>}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Wat de bron zegt
            </p>
            {voorstel ? (
              <>
                <p className="mt-1 text-sm font-mono break-words">{signaal.bron_waarde}</p>
                {/* Ook een gevulde bronwaarde wordt NIET doorgevoerd. Zie de kop
                    van useSubsidieSignalen.ts: de bewaking stelt geen waarden
                    voor, en een scherm dat dat wel zou doen, verzint. */}
                <p className="mt-2 text-xs text-amber-700">
                  Deze waarde wordt niet automatisch doorgevoerd — neem hem zelf over.
                </p>
              </>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                Geen waarde vastgesteld. De bewaking controleert of de opgeslagen waarde nog
                op de pagina staat, maar leidt er nooit zelf een nieuwe waarde uit af.
              </p>
            )}
          </div>
        </div>

        {/* De brontekst ernaast: het fragment waarin gezocht is. */}
        {signaal.bron_tekst && (
          <details className="rounded-md border">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
              Brontekst ({signaal.bron_tekst.length} tekens)
            </summary>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words border-t px-3 py-2 text-xs text-muted-foreground">
              {signaal.bron_tekst}
            </pre>
          </details>
        )}

        {/* Wat er ná het besluit nog moet gebeuren. Zonder deze regel zou
            "bevestigen" een knop zijn die succes meldt en niets verandert. */}
        <div className="rounded-md bg-muted/60 p-3 text-sm">
          <p className="font-medium">Wat jij hierna zelf doet</p>
          <p className="text-muted-foreground">{vervolg.wat}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {vervolg.plek === 'beheer' && (
              <Button variant="outline" size="sm" onClick={onNaarBeheer}>
                Naar tabblad Beheer <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {signaal.bron_url && (
              <a href={signaal.bron_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Bronpagina
                </Button>
              </a>
            )}
          </div>
        </div>

        <Textarea
          value={notitie}
          onChange={e => setNotitie(e.target.value)}
          placeholder="Notitie bij dit besluit (optioneel) — komt in afhandel_notitie"
          rows={2}
          disabled={!magAfhandelen || isBezig}
        />

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!magAfhandelen || isBezig}
            onClick={() => onAfhandelen({ id: signaal.id, besluit: 'bevestigd', notitie: notitie || null })}
          >
            <Check className="h-4 w-4 mr-1" /> Bevestigen
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!magAfhandelen || isBezig}
            onClick={() => onAfhandelen({ id: signaal.id, besluit: 'afgewezen', notitie: notitie || null })}
          >
            <X className="h-4 w-4 mr-1" /> Afwijzen
          </Button>
          {/* Wat de klik dan wél doet, en waarom dat voor BEIDE schrijvers geldt.
              Dat laatste was de vangst van een scepticus: deze belofte was eerst
              onderbouwd met alleen de edge-functie, en juist die wordt door niets
              aangeroepen — de enige geplande schrijver is de pg_cron-job.

              `schrijfSignaal` in supabase/functions/subsidie-bewaking/index.ts
              pakt het laatste signaal per sleutel ongeacht status en meldt niets
              nieuws zolang huidige_waarde en bron_waarde gelijk blijven.
              `public.sub_bewaking_ouderdom()` deed dat NIET: zijn `on conflict
              ... where status = 'concept'` matchte na een besluit niet meer, dus
              volgde er een verse rij mét notificatie. Elke maandag opnieuw.
              Migratie 080 legt daar dezelfde toets in. Bewezen met een
              zelfterugrollende test: eerste run meldt 1, na afwijzing meldt de
              tweede run 0 en gaan er 0 notificaties uit. */}
          <span className="text-xs text-muted-foreground">
            Beide raken alleen dit signaal, nooit de regeling zelf. De bewaking meldt het
            pas opnieuw als de waarde verschuift.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
